import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import type { PageEnvelope } from '../../../components';
import { toEnvelope, toPageParams, type NetPaged } from '../../shared/netPaged';
import type {
  EntraLinkUserResponse,
  MergeUsersRequest,
  MergeUsersResponse,
  SetEntraLinkRequest,
  SiteAdminListItem,
  SiteUserCreateRequest,
  SiteUserResponse,
  SiteUserSearchResponse,
} from '../types';

/**
 * TanStack Query hooks for the Site Admin identity tools (FE slice 20 §F1–F4).
 * All routes are under `/api/v1/site/users*`, `RequiresSiteAdministrator`. The
 * env-gated MSW seam and the real API share these hooks unchanged.
 *
 * **Paging shape.** `GET /site/users?role=SiteAdministrator` (block 085) returns
 * the .NET `Paged<T>` shape (`{ items, totalCount, page, pageSize, hasMore }`),
 * unlike the slice-16/21 console lists which return the `{ items, total, take,
 * skip }` envelope. {@link toEnvelope} adapts the former to the latter so the
 * shared `PagedTable` (skip/take) works and paging params round-trip as the
 * `page`/`pageSize` the endpoint actually reads.
 */
const IDENTITY_KEY = ['site', 'identity'] as const;

// ── Cross-customer user search (§F1/F2/F3 pickers) ───────────────────────────
/** Optional target filters for {@link useUserSearch}. */
export interface UserSearchFilters {
  /** Exclude deactivated users — the impersonation launchers, which can't use them. */
  activeOnly?: boolean;
  /** Exclude internal DecisionPoint users (those with no customer scope). */
  excludeInternal?: boolean;
}

/**
 * `GET /site/users/search?q=&take=` — type-ahead across all tenants. Disabled
 * until the trimmed query reaches the backend's 2-char minimum, so a short
 * query never fires a 400.
 *
 * The server matches the term against email, first name, last name **and**
 * employee number, splitting it on whitespace so a full name resolves (BUG-042);
 * it also answers `hasMore` when the `take` cap hid further matches. The optional
 * filters let the impersonation launcher stop offering targets the API rejects,
 * while the merge / Entra-link / grant pickers keep the unfiltered set they need.
 */
export function useUserSearch(query: string, take = 20, filters: UserSearchFilters = {}) {
  const q = query.trim();
  const enabled = q.length >= 2;
  const { activeOnly, excludeInternal } = filters;
  return useQuery<SiteUserSearchResponse & { appliedTerm: string }>({
    queryKey: [...IDENTITY_KEY, 'search', q, take, { activeOnly, excludeInternal }],
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<SiteUserSearchResponse>('/site/users/search', {
        params: {
          q,
          take,
          ...(activeOnly === undefined ? {} : { activeOnly }),
          ...(excludeInternal === undefined ? {} : { excludeInternal }),
        },
        signal,
      });
      // `appliedTerm` marks which term this page answers (BUG-043).
      return { ...data, appliedTerm: q };
    },
  });
}

// ── Entra link (§F1) ─────────────────────────────────────────────────────────
export function useSetEntraLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { userId: string; body: SetEntraLinkRequest }) => {
      const { data } = await apiClient.post<EntraLinkUserResponse>(
        `/site/users/${args.userId}/entra-link`,
        args.body,
      );
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...IDENTITY_KEY, 'search'] }),
  });
}

export function useClearEntraLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { userId: string; reason: string }) => {
      const { data } = await apiClient.delete<EntraLinkUserResponse>(
        `/site/users/${args.userId}/entra-link`,
        { params: { reason: args.reason } },
      );
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...IDENTITY_KEY, 'search'] }),
  });
}

/** `GET /site/users/by-entra-oid/{oid}` as an on-demand lookup (button-triggered). */
export function useReverseLookup() {
  return useMutation({
    mutationFn: async (oid: string) => {
      const { data } = await apiClient.get<EntraLinkUserResponse>(
        `/site/users/by-entra-oid/${encodeURIComponent(oid)}`,
      );
      return data;
    },
  });
}

// ── User merge (§F2) ─────────────────────────────────────────────────────────
export function useMergeUsers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: MergeUsersRequest) => {
      const { data } = await apiClient.post<MergeUsersResponse>('/site/users/merge', body);
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: IDENTITY_KEY }),
  });
}

// ── SiteAdmin management (§F3 grant/revoke, §F4 create/list) ─────────────────
export interface SiteAdminListParams {
  activeOnly?: boolean;
  search?: string;
  take?: number;
  skip?: number;
}

/** `GET /site/users?role=SiteAdministrator` → adapted PageEnvelope for PagedTable. */
export function useSiteAdmins(params: SiteAdminListParams = {}) {
  const { activeOnly, search, take = 50, skip = 0 } = params;
  const { page, pageSize } = toPageParams(skip, take);
  return useQuery<PageEnvelope<SiteAdminListItem>>({
    queryKey: [...IDENTITY_KEY, 'siteAdmins', { activeOnly, search, page, pageSize }],
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<NetPaged<SiteAdminListItem>>('/site/users', {
        params: {
          role: 'SiteAdministrator',
          ...(activeOnly ? { activeOnly: true } : {}),
          ...(search ? { search } : {}),
          page,
          pageSize,
        },
        signal,
      });
      return toEnvelope(data);
    },
  });
}

export function useCreateSiteAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SiteUserCreateRequest) => {
      const { data } = await apiClient.post<SiteUserResponse>('/site/users', body);
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...IDENTITY_KEY, 'siteAdmins'] }),
  });
}

export function useGrantSiteAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.post(`/site/users/${userId}/roles/SiteAdministrator`);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...IDENTITY_KEY] }),
  });
}

export function useRevokeSiteAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.delete(`/site/users/${userId}/roles/SiteAdministrator`);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...IDENTITY_KEY, 'siteAdmins'] }),
  });
}
